import { downloadBlob } from 'vopay';

// Client-side export helpers for TransactionHistory (ID-13367). Ported from client-dashboard's
// useFiledDownload, trimmed to the eight the feature actually uses: PDF/CSV come back from the API as a
// data URI / base64 and download through an anchor; XLS/DOC/TXT/XML/print scrape the hidden export table's
// DOM (id `export-transaction-history-table`). Plain functions, no jspdf (the feature never used those
// paths).

export function downloadJson(data: unknown, fileName: string) {
  downloadBlob({
    data: JSON.stringify(data, null, 4),
    fileName: `${fileName.replace('.json', '')}.json`,
    fileType: 'text/json',
  });
}

export function downloadPdf(data: string, fileName: string) {
  const downloadLink = document.createElement('a');
  downloadLink.href = data;
  downloadLink.setAttribute('download', `${fileName.replace('.pdf', '')}.pdf`);
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
}

export function downloadCsv(data: string, fileName: string) {
  const downloadLink = document.createElement('a');
  downloadLink.href = data;
  downloadLink.setAttribute('download', `${fileName.replace('.csv', '')}.csv`);
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
}

export function downloadXls(id: string, fileName: string) {
  const table = document.getElementById(id);
  if (!table) return;

  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:x="urn:schemas-microsoft-com:office:excel"
          xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>${fileName}</x:Name>
                <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <meta charset="UTF-8">
      </head>
      <body>
        ${table.outerHTML}
      </body>
    </html>
  `;

  const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
  const url = URL.createObjectURL(blob);

  const downloadLink = document.createElement('a');
  downloadLink.href = url;
  downloadLink.download = `${fileName}.xls`;

  document.body.appendChild(downloadLink);
  downloadLink.click();

  document.body.removeChild(downloadLink);
  URL.revokeObjectURL(url);
}

export function downloadDoc(id: string, fileName: string) {
  const tableElement = document.getElementById(id);
  if (!tableElement) return;

  const tableHTML = tableElement.outerHTML;

  const header = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:w="urn:schemas-microsoft-com:office:word"
          xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8"></head><body>`;

  const footer = `</body></html>`;

  const htmlContent = header + tableHTML + footer;

  const blob = new Blob(['﻿', htmlContent], {
    type: 'application/msword',
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}.doc`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadTxt(id: string, fileName: string) {
  const table = document.getElementById(id)?.outerHTML;

  const strippedHtml = table
    ?.replaceAll('</tr>', '\r')
    .replaceAll('</th>', ',')
    .replaceAll('</td>', ',')
    .replaceAll('</th>', ',')
    .replaceAll(/<[^>]+>/g, '')
    .replaceAll(',,', ',');

  const file = `${fileName}.txt`;

  const txtFile = new Blob([strippedHtml as BlobPart], { type: 'text/plain' });
  const downloadLink = document.createElement('a');
  downloadLink.download = file;
  downloadLink.href = window.URL.createObjectURL(txtFile);
  downloadLink.style.display = 'none';
  document.body.appendChild(downloadLink);
  downloadLink.click();
}

export function downloadXml(id: string, fileName: string) {
  const table = document.getElementById(id);
  if (!table) return;

  const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<root>
  <tableData><![CDATA[${table.outerHTML}]]></tableData>
</root>`;

  const blob = new Blob([xmlContent], { type: 'application/xml' });
  const url = URL.createObjectURL(blob);

  const downloadLink = document.createElement('a');
  downloadLink.href = url;
  downloadLink.download = `${fileName}.xml`;

  document.body.appendChild(downloadLink);
  downloadLink.click();

  document.body.removeChild(downloadLink);
  URL.revokeObjectURL(url);
}

function getStyleSheets() {
  const styleSheets = Array.from(document.styleSheets);

  const styles = styleSheets.map((styleSheet) => {
    if (styleSheet.href) {
      return styleSheet.href;
    }

    const rules = Array.from(styleSheet.cssRules).map((rule) => rule.cssText);

    return rules.join('\n');
  });

  return styles.join('\n');
}

function getScripts() {
  const scripts = Array.from(document.scripts);
  const scriptSrcs = scripts.map((script) => script.src);

  return scriptSrcs.join('\n');
}

function isURL(str: string) {
  const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;

  return urlPattern.test(str);
}

export function printFile(id: string) {
  const table = document.getElementById(id)?.outerHTML;
  if (!table) return;

  const overlay = document.createElement('div');
  overlay.id = 'printOverlay';

  Object.assign(overlay.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    background: 'rgba(0,0,0,0.4)',
    zIndex: '9999',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '18px',
    fontFamily: 'sans-serif',
  });

  overlay.textContent = 'Preparing print view...';
  document.body.appendChild(overlay);

  const styles = getStyleSheets();
  const scripts = getScripts();
  const isExternal = isURL(styles);

  const newWindow = window.open('', '_blank');
  if (!newWindow) return;

  const html = `
    <html>
      <head>
        ${isExternal ? `<link rel="stylesheet" href="${styles}">` : `<style>${styles}</style>`}
        <style>.hiddenFormsPrint { display: none; }</style>
      </head>
      <body>
        ${table}
        <script>${scripts}</script>
      </body>
    </html>
  `;

  newWindow.document.write(html);
  newWindow.document.close();

  newWindow.onload = () => {
    newWindow.print();

    newWindow.onafterprint = () => {
      newWindow.close();
      document.getElementById('printOverlay')?.remove();
    };

    setTimeout(() => {
      newWindow.close();
      document.getElementById('printOverlay')?.remove();
    }, 3000);
  };
}
